import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const initialAssets = [
  { 
    id: 1, 
    type: 'Video', 
    name: 'Hero Explainer V2', 
    status: 'Needs Review', 
    assignee: 'Sarah J.', 
    tags: ['Website', 'Social'], 
    img: 'https://images.unsplash.com/photo-1550745165-9bc0b252726f?auto=format&fit=crop&q=80&w=800' 
  },
  { 
    id: 2, 
    type: 'Design', 
    name: 'App Store Set', 
    status: 'Approved', 
    assignee: 'Mike T.', 
    tags: ['Mobile'], 
    img: 'https://images.unsplash.com/photo-1586717791821-3f44a563dc4c?auto=format&fit=crop&q=80&w=800' 
  },
  { 
    id: 3, 
    type: 'Copy', 
    name: 'Launch Day Emails', 
    status: 'In Progress', 
    assignee: 'Emma W.', 
    tags: ['Email'], 
    img: 'https://images.unsplash.com/photo-1555421689-491a97ff2040?auto=format&fit=crop&q=80&w=800' 
  },
  { 
    id: 4, 
    type: 'Design', 
    name: 'Social Media Kit', 
    status: 'Approved', 
    assignee: 'Mike T.', 
    tags: ['Social'], 
    img: 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7?auto=format&fit=crop&q=80&w=800' 
  },
];

const AssetRoom = () => {
  const [filter, setFilter] = useState('All');
  const [assets, setAssets] = useState(initialAssets);

  const handleApprove = (id) => {
    setAssets(assets.map(a => a.id === id ? { ...a, status: 'Approved' } : a));
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Approved': return 'text-green-400 bg-green-400/10 border-green-400/20';
      case 'Needs Review': return 'text-rocket-400 bg-rocket-400/10 border-rocket-400/20';
      default: return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'Video': return 'Video';
      case 'Design': return 'Image';
      case 'Copy': return 'FileText';
      default: return 'File';
    }
  };

  const filtered = filter === 'All' ? assets : assets.filter(a => a.status === filter);

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-10">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 border-b border-slate-800 pb-6">
        <div>
          <h2 className="text-2xl font-bold text-ice">Asset Room</h2>
          <p className="text-sm text-slate-400 font-mono mt-1 uppercase tracking-widest">Payload Inventory Control</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {['All', 'Needs Review', 'Approved'].map(f => (
            <button 
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded text-xs font-mono transition-all ${
                filter === f ? 'bg-rocket-500 text-white shadow-[0_0_10px_rgba(249,115,22,0.3)]' : 'bg-slate-900 text-slate-400 hover:text-ice border border-slate-800'
              }`}
            >
              {f.toUpperCase()}
            </button>
          ))}
          <button className="bg-rocket-600/20 text-rocket-500 border border-rocket-500/30 px-4 py-1.5 rounded text-xs font-bold flex items-center hover:bg-rocket-600/30 transition-colors">
            <SafeIcon name="Plus" className="mr-1" /> UPLOAD
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {filtered.map((asset, i) => (
          <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            key={asset.id} 
            className="glass-panel rounded-xl overflow-hidden group border border-slate-800 hover:border-rocket-500/50 transition-all duration-300 flex flex-col h-full"
          >
            <div className="h-44 relative overflow-hidden bg-slate-950">
              <img 
                src={asset.img} 
                alt={asset.name} 
                className="w-full h-full object-cover opacity-50 group-hover:opacity-80 group-hover:scale-110 transition-all duration-700" 
              />
              <div className="absolute top-3 right-3">
                <span className={`text-[9px] uppercase font-bold px-2 py-1 rounded border backdrop-blur-md ${getStatusColor(asset.status)}`}>
                  {asset.status}
                </span>
              </div>
              <div className="absolute bottom-3 left-3 bg-slate-950/80 p-2 rounded backdrop-blur-md border border-slate-800">
                <SafeIcon name={getIcon(asset.type)} className="text-rocket-400" />
              </div>
            </div>

            <div className="p-5 flex flex-col flex-1">
              <h3 className="font-bold text-ice truncate mb-1 group-hover:text-rocket-400 transition-colors">{asset.name}</h3>
              <div className="flex items-center text-[10px] text-slate-500 mb-4 font-mono">
                <div className="w-5 h-5 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center mr-2 text-[8px] font-bold text-rocket-500">
                   {asset.assignee.split(' ')[0][0]}{asset.assignee.split(' ')[1][0]}
                </div>
                {asset.assignee}
              </div>
              
              <div className="flex flex-wrap gap-1 mb-6 mt-auto">
                {asset.tags.map(tag => (
                  <span key={tag} className="text-[9px] font-mono bg-slate-900 text-slate-500 px-2 py-0.5 rounded border border-slate-800">{tag}</span>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-800 flex justify-between items-center">
                <button className="text-slate-500 hover:text-ice transition-colors"><SafeIcon name="Eye" size={16} /></button>
                {asset.status === 'Needs Review' ? (
                  <button 
                    onClick={() => handleApprove(asset.id)}
                    className="text-[10px] font-bold bg-rocket-600 hover:bg-rocket-500 text-white px-4 py-1.5 rounded transition-all shadow-[0_0_10px_rgba(234,88,12,0.3)]"
                  >
                    APPROVE
                  </button>
                ) : (
                  <button className="text-slate-500 hover:text-slate-300"><SafeIcon name="MoreHorizontal" /></button>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default AssetRoom;